import React, { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Box, Button, Slider, Typography, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestoreIcon from '@mui/icons-material/Restore';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import SaveIcon from '@mui/icons-material/Save';
import BrushIcon from '@mui/icons-material/Brush';
import PanToolIcon from '@mui/icons-material/PanTool';

const MaskingCanvas = ({ image, onSaveMask, onClose }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [brushMode, setBrushMode] = useState(true);
  const [panStart, setPanStart] = useState(null);
  const [cursorPos, setCursorPos] = useState(null); // State for cursor position {x, y} relative to canvas element
  const [isCursorVisible, setIsCursorVisible] = useState(false); // State for cursor visibility

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const aspectRatio = imageRef.current.naturalWidth / imageRef.current.naturalHeight;
    const maxWidth = canvas.parentElement.offsetWidth * 0.95;
    const maxHeight = canvas.parentElement.offsetHeight * 0.95;

    let newWidth = maxWidth;
    let newHeight = maxWidth / aspectRatio;

    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = maxHeight * aspectRatio;
    }
    
    canvas.width = newWidth;
    canvas.height = newHeight;
    setCtx(ctx);

    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, newWidth, newHeight);
    }

    saveHistory(ctx);
  }, []);

  useEffect(() => {
    if (image) {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.src = image + '?not-from-cache-please';
      img.onload = () => {
        imageRef.current = img;
        setupCanvas();
      };
      img.onerror = (e) => {
        console.error('Resim yüklenemedi:', e);
        fetch(image, { mode: 'cors' })
          .then(response => response.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob);
            const img = new window.Image();
            img.crossOrigin = "anonymous";
            img.src = url;
            img.onload = () => {
              imageRef.current = img;
              setupCanvas();
            };
          })
          .catch(error => {
            console.error('CORS ile resim yüklenemedi:', error);
          });
      };
    }
  }, [image, setupCanvas]);

  const saveHistory = useCallback((currentCtx) => {
    if (!currentCtx) return;
    const canvas = currentCtx.canvas;
    const newHistory = history.slice(0, historyIndex + 1);
    setHistory([...newHistory, canvas.toDataURL()]);
    setHistoryIndex(newHistory.length);
  }, [history, historyIndex]);

  const restoreHistory = useCallback((index) => {
    if (!ctx || index < 0 || index >= history.length) return;
    const canvas = ctx.canvas;
    const image = new Image();
    image.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      setHistoryIndex(index);
    };
    image.src = history[index];
  }, [ctx, history]);

  const handleUndo = useCallback(() => {
    restoreHistory(historyIndex - 1);
  }, [historyIndex, restoreHistory]);

  const handleRedo = useCallback(() => {
    restoreHistory(historyIndex + 1);
  }, [historyIndex, restoreHistory]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!ctx) return;
    if (brushMode) {
      setDrawing(true);
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = (e.touches[0].clientX - rect.left) / zoom;
      const y = (e.touches[0].clientY - rect.top) / zoom;
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      setPanStart({
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
        offsetX,
        offsetY
      });
    }
  }, [ctx, brushMode, zoom, offsetX, offsetY]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!ctx) return;

    if (drawing) {
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = (e.touches[0].clientX - rect.left) / zoom;
      const y = (e.touches[0].clientY - rect.top) / zoom;

      ctx.lineWidth = brushSize / zoom;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgb(255, 0, 0)';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else if (panStart) {
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const y = e.touches[0].clientY - rect.top;

      setOffsetX(panStart.offsetX + (x - panStart.x) / zoom);
      setOffsetY(panStart.offsetY + (y - panStart.y) / zoom);
    }
  }, [ctx, drawing, brushSize, panStart, zoom, offsetX, offsetY]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (drawing) {
      setDrawing(false);
      saveHistory(ctx);
    }
    setPanStart(null);
  }, [drawing, ctx, saveHistory]);

  const handleMouseDown = useCallback((e) => {
    if (!ctx) return;

    if (brushMode) {
      setDrawing(true);
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      setPanStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        offsetX,
        offsetY
      });
    }
  }, [ctx, brushMode, zoom, offsetX, offsetY]);

  const handleMouseMove = useCallback((e) => {
    if (!ctx) return;

    if (drawing) {
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;
      
      ctx.lineWidth = brushSize / zoom;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(138, 43, 226, 0.5)';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else if (panStart) {
      const canvas = ctx.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setOffsetX(panStart.offsetX + (x - panStart.x) / zoom);
      setOffsetY(panStart.offsetY + (y - panStart.y) / zoom);
    }

    // Update cursor position for the visual indicator relative to the parent container
    const canvas = ctx.canvas;
    const parentBox = canvas.parentElement; // Get the immediate parent Box
    if (parentBox) {
      const parentRect = parentBox.getBoundingClientRect();
      const mouseX = e.clientX - parentRect.left; // Position relative to parent Box left
      const mouseY = e.clientY - parentRect.top;  // Position relative to parent Box top
      setCursorPos({ x: mouseX, y: mouseY });
      setIsCursorVisible(true);
    }

  }, [ctx, drawing, brushSize, zoom, offsetX, offsetY, panStart]);

  const handleMouseLeave = useCallback(() => {
    setIsCursorVisible(false);
    if (drawing) { // Also stop drawing if mouse leaves while drawing
      setDrawing(false);
      saveHistory(ctx);
    }
    setPanStart(null); // Ensure panning stops if mouse leaves
  }, [drawing, ctx, saveHistory]);

  const handleMouseUp = useCallback(() => {
    if (drawing) {
      setDrawing(false);
      saveHistory(ctx);
    }
    setPanStart(null);
  }, [drawing, ctx, saveHistory]);

  const handleSave = async () => {
    const canvas = canvasRef.current;
    // Create a canvas for the final mask
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = imageRef.current.naturalWidth;
    maskCanvas.height = imageRef.current.naturalHeight;
    const maskCtx = maskCanvas.getContext('2d');
    

    // Create a temporary canvas for processing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');

    // Clear the temp canvas first
    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Draw only the current canvas content (the strokes)
    tempCtx.drawImage(canvas, 0, 0);

    // Get the image data
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;

    // Create a new ImageData for the mask
    const maskData = new ImageData(tempCanvas.width, tempCanvas.height);
    const maskPixels = maskData.data;

    // Process each pixel
    for (let i = 0; i < data.length; i += 4) {
      // Check specifically for purple color (138, 43, 226) with opacity
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // Check if pixel is purple-ish and has opacity - use stricter criteria
      if (a > 50 && r > 100 && g < 100 && b > 100) {
        maskPixels[i] = 255;     // R
        maskPixels[i + 1] = 255; // G
        maskPixels[i + 2] = 255; // B
        maskPixels[i + 3] = 255; // A
      } else {
        maskPixels[i] = 0;       // R
        maskPixels[i + 1] = 0;   // G
        maskPixels[i + 2] = 0;   // B
        maskPixels[i + 3] = 0;   // A
      }
    }

    // Apply noise reduction to remove small isolated areas
    const removeNoise = (data, width, height, threshold = 100) => {
      const result = new Uint8ClampedArray(data.length);
      
      // First copy the original data
      for (let i = 0; i < data.length; i++) {
        result[i] = data[i];
      }
      
      // For each pixel, check if it's an isolated area
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = (y * width + x) * 4;
          
          // Skip if pixel is already black
          if (data[idx] === 0) continue;
          
          // Count white neighbors
          let whiteNeighbors = 0;
          
          // Check 8 surrounding pixels
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue; // Skip self
              
              const nx = x + dx;
              const ny = y + dy;
              const nidx = (ny * width + nx) * 4;
              
              if (data[nidx] > 0) {
                whiteNeighbors++;
              }
            }
          }
          
          // If pixel has few white neighbors, make it black
          if (whiteNeighbors < 3) {
            result[idx] = 0;     // R
            result[idx + 1] = 0; // G
            result[idx + 2] = 0; // B
            result[idx + 3] = 0; // A
          }
        }
      }
      
      return result;
    };
    
    // Apply noise reduction
    const cleanedMaskData = removeNoise(maskPixels, tempCanvas.width, tempCanvas.height);
    
    // Put the cleaned mask data back
    for (let i = 0; i < maskPixels.length; i++) {
      maskPixels[i] = cleanedMaskData[i];
    }

    // Put the black and white mask back
    tempCtx.putImageData(maskData, 0, 0);
    
    // Apply a second pass of noise reduction using canvas operations
    const secondPassCanvas = document.createElement('canvas');
    secondPassCanvas.width = tempCanvas.width;
    secondPassCanvas.height = tempCanvas.height;
    const secondPassCtx = secondPassCanvas.getContext('2d');
    
    // Draw the first pass result
    secondPassCtx.drawImage(tempCanvas, 0, 0);
    
    // Apply a small blur to connect nearby areas and smooth edges
    secondPassCtx.filter = 'blur(1px)';
    secondPassCtx.drawImage(secondPassCanvas, 0, 0);
    
    // Apply threshold to make it black and white again
    const secondPassData = secondPassCtx.getImageData(0, 0, secondPassCanvas.width, secondPassCanvas.height);
    const secondPassPixels = secondPassData.data;
    
    for (let i = 0; i < secondPassPixels.length; i += 4) {
      // Apply threshold - if not clearly white, make it black
      if (secondPassPixels[i] < 200) {
        secondPassPixels[i] = 0;     // R
        secondPassPixels[i + 1] = 0; // G
        secondPassPixels[i + 2] = 0; // B
        secondPassPixels[i + 3] = 0; // A
      } else {
        secondPassPixels[i] = 255;     // R
        secondPassPixels[i + 1] = 255; // G
        secondPassPixels[i + 2] = 255; // B
        secondPassPixels[i + 3] = 255; // A
      }
    }
    
    secondPassCtx.putImageData(secondPassData, 0, 0);
    
    // Use the second pass result for the final mask
    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(secondPassCanvas, 0, 0);

    // Scale the mask to match original image dimensions
    maskCtx.drawImage(
      tempCanvas,
      0, 0, canvas.width, canvas.height,
      0, 0, maskCanvas.width, maskCanvas.height
    );
    
    maskCanvas.toBlob(async (blob) => {
      try {
        const fileName = `mask-${Date.now()}.png`;
        const { data, error } = await supabase.storage
          .from('mask-images')
          .upload(fileName, blob);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('mask-images')
          .getPublicUrl(fileName);
        onSaveMask(null, publicUrl);
      } catch (error) {
        console.error("Maske kaydedilemedi:", error);
      }
    }, "image/png");
  };

  const handleClear = () => {
    if (!ctx) return;
    const canvas = ctx.canvas;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    }
    saveHistory(ctx);
  };

  const handleZoom = (direction) => {
    const newZoom = zoom + (direction === 'in' ? 0.1 : -0.1);
    setZoom(Math.max(0.2, Math.min(3, newZoom)));
  };

  const handleWheel = (e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const newBrushSize = brushSize + (e.deltaY > 0 ? -2 : 2);
      setBrushSize(Math.min(100, Math.max(1, newBrushSize)));
    }
  };

  const handleTogglePan = () => {
    setBrushMode(!brushMode);
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(18, 18, 23, 0.98)' : 'rgba(255, 255, 255, 0.98)',
        zIndex: 1500,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '8px',
      }}
      onWheel={handleWheel}
      onTouchMove={(e) => e.preventDefault()}
      style={{ touchAction: 'none' }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          zIndex: 1600,
          padding: '10px',
          gap: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
        }}
      >
        <Tooltip title="Yakınlaştır">
          <IconButton 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              }
            }} 
            onClick={() => handleZoom('in')}
          >
            <ZoomInIcon/>
          </IconButton>
        </Tooltip>
        <Tooltip title="Uzaklaştır">
          <IconButton 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              }
            }} 
            onClick={() => handleZoom('out')}
          >
            <ZoomOutIcon/>
          </IconButton>
        </Tooltip>
        <Tooltip title="Sıfırla">
          <IconButton 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              }
            }} 
            onClick={() => setZoom(1)}
          >
            <RestoreIcon/>
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          display: 'flex',
          zIndex: 1600,
          flexDirection: 'column',
          gap: '10px',
          padding: '10px 10px',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
        }}
      >
        <Tooltip title={brushMode ? "Fırça Modu" : "Kaydırma Modu"}>
          <IconButton 
            onClick={handleTogglePan} 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              }
            }}
          >
            {brushMode ? <BrushIcon /> : <PanToolIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Temizle">
          <IconButton 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              }
            }} 
            onClick={handleClear}
          >
            <CleaningServicesIcon/>
          </IconButton>
        </Tooltip>
        <Tooltip title="Geri Al">
          <IconButton 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(138, 43, 226, 0.4)',
              }
            }} 
            onClick={handleUndo} 
            disabled={historyIndex <= 0}
          >
            <UndoIcon/>
          </IconButton>
        </Tooltip>
        <Tooltip title="İleri Al">
          <IconButton 
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.9)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(138, 43, 226, 0.4)',
              }
            }} 
            onClick={handleRedo} 
            disabled={historyIndex >= history.length - 1}
          >
            <RedoIcon/>
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: '90%',
          height: '90%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '2px solid rgba(138, 43, 226, 0.3)',
          borderRadius: '8px',
          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(10, 10, 15, 0.7)' : 'rgba(245, 245, 245, 0.7)',
        }}
      >
        {/* Brush size indicator */}
        {isCursorVisible && brushMode && cursorPos && (
          <Box
            sx={{
              position: 'absolute',
              left: cursorPos.x,
              top: cursorPos.y,
              width: `${brushSize}px`,
              height: `${brushSize}px`,
              border: '1px solid rgba(138, 43, 226, 0.9)', // Purple border
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)', // Center on cursor
              pointerEvents: 'none', // Allow clicks to pass through
              zIndex: 10, // Ensure it's above the canvas but below controls
              transition: 'width 0.05s ease, height 0.05s ease', // Smooth size change
              mixBlendMode: 'difference', // Make it visible on different backgrounds
            }}
          />
        )}
        <canvas
          ref={canvasRef}
          style={{
            cursor: brushMode ? 'crosshair' : 'grab',
            transform: `scale(${zoom}) translate(${offsetX}px, ${offsetY}px)`,
            transformOrigin: 'top left',
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave} // Add mouse leave handler
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
        <img
          src={image}
          ref={imageRef}
          style={{ display: 'none' }}
          alt="Gizli tuval resmi"
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '95%',
          maxWidth: '1200px',
          display: 'flex',
          borderRadius: '8px',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          padding: { xs: '16px', sm: '10px 20px' },
          gap: { xs: 2, sm: 0 },
          zIndex: 1000,
          background: 'rgba(138, 43, 226, 0.9)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
            Fırça Boyutu: {brushSize}
          </Typography>
          <Slider
            value={brushSize}
            onChange={(e, size) => setBrushSize(size)}
            min={1}
            max={100}
            sx={{
              width: 150,
              color: 'white',
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                backgroundColor: 'white',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)'
                }
              },
              '& .MuiSlider-track': {
                backgroundColor: 'white'
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' }
        }}>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: 'white',
              color: 'rgba(138, 43, 226, 0.9)',
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '1rem', sm: 'inherit' },
              padding: { xs: '12px', sm: '8px 24px' },
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }
            }}
            startIcon={<SaveIcon/>}
          >
            Kaydet
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: 'white',
              color: 'white',
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '1rem', sm: 'inherit' },
              padding: { xs: '12px', sm: '8px 24px' },
              fontWeight: 600,
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
            onClick={onClose}
          >
            Kapat
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MaskingCanvas;
